import { wrapWithEventPropertiesPrefix } from '@adsk/exo-skeleton-api/internal';

import { EventContext } from '../monitoring';
import { getDTMonitoringContext } from '../monitoringContext';

interface DynatraceEvent {
  scope?: string;
  context?: EventContext;
}

//This function is used to spread the scope property value to higher level since dynatrace doesn't support nested object
export function spreadScopedPropertiesForRootLevel(context: EventContext) {
  if (!context.scope) {
    return context;
  }

  const scope = context.scope;

  // We add custom property with scope as a key in report api - https://git.autodesk.com/BIM360/acs-sc-web-platform/blob/master/libs/exo-skeleton-api/monitoring.ts#L77
  // We need to spread the scope property value to higher level since dynatrace doesn't support nested object
  const dynatraceContext: EventContext = {
    ...context,
    ...(context?.[scope] || {}),
  };

  // We need to remove scope field from context to avoid duplication once they will support objects (currently doesn't impact anything)
  if (scope && context.hasOwnProperty(scope)) {
    delete dynatraceContext[scope]; //:TODO: Remove scope field after spread value, currently don't work
  }

  return dynatraceContext;
}

// Pay attention that this is a global filter. It is not case-sensitive and uses contains logic
const FILTERED_ERROR_MESSAGES = [
  'Pending user action for user consent preferences',
  'ResizeObserver loop completed with undelivered notifications',
].map((message) => message.toLowerCase());

function shouldFilterOutException(event: Record<string, unknown>): boolean {
  const message = (
    event?.['exception.message'] as string | undefined
  )?.toLowerCase();
  if (!message) {
    return false;
  }

  return FILTERED_ERROR_MESSAGES.some((filteredMessage) =>
    message.includes(filteredMessage),
  );
}

export const addEventModifier = (
  event: Record<string, unknown>,
): Record<string, unknown> | null => {
  try {
    if (shouldFilterOutException(event)) {
      return null;
    }

    const result = {
      ...event,
      ...wrapWithEventPropertiesPrefix({
        ...getDTMonitoringContext(),
      }),
    };

    return result;
  } catch (e) {
    console.error(`Failed to use dynatrace addEventModifier: ${e}`);
    return event;
  }
};

export function sendEventToDynatrace({ scope, context = {} }: DynatraceEvent) {
  try {
    if (!window.dynatrace) {
      return;
    }

    const dynatraceContext: EventContext =
      spreadScopedPropertiesForRootLevel(context);

    window.dynatrace.sendEvent({
      ...wrapWithEventPropertiesPrefix({ scope }),
      ...dynatraceContext,
    });
  } catch (e) {
    console.error(`Failed to send event to Dynatrace: ${e}`);
  }
}
